// src/authpages/timetables/CalendarExport.js
import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Download, Calendar, FileText, Image } from 'lucide-react';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, 
         BorderStyle, HeadingLevel, AlignmentType, WidthType } from 'docx';
import { saveAs } from 'file-saver';

/**
 * Converts calendar events to iCalendar (.ics) format
 * @param {Array} events - Array of calendar events
 * @param {string} calendarName - Name of the calendar
 * @returns {string} iCalendar formatted string
 */
const convertToICS = (events, calendarName = "StudyAce Calendar") => {
  // iCalendar format requires specific formatting
  const formatDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 15) + 'Z';
  };

  // Start building the iCalendar file
  let icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StudyAce//Calendar//EN',
    `X-WR-CALNAME:${calendarName}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH'
  ];

  // Add each event
  events.forEach(event => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    
    icsContent = icsContent.concat([
      'BEGIN:VEVENT',
      `UID:${event.id || Math.random().toString(36).substring(2, 11)}`,
      `DTSTAMP:${formatDate(new Date())}`,
      `DTSTART:${formatDate(start)}`,
      `DTEND:${formatDate(end)}`,
      `SUMMARY:${event.title || 'Untitled Event'}`,
      `DESCRIPTION:${event.provider || ''}`
    ]);

    // Add color if available
    if (event.color) {
      icsContent.push(`X-APPLE-CALENDAR-COLOR:${event.color}`);
    }

    // Close the event
    icsContent.push('END:VEVENT');
  });

  // Close the calendar
  icsContent.push('END:VCALENDAR');

  // Join all lines with proper line endings
  return icsContent.join('\r\n');
};

/**
 * Creates a Word document (.docx) from calendar events
 * @param {Array} events - Array of calendar events
 * @param {string} calendarName - Name of the calendar
 */
const exportToWord = async (events, calendarName) => {
  // Create new document
  const doc = new Document({
    title: calendarName,
    description: 'Calendar export from StudyAce',
    styles: {
      paragraphStyles: [
        {
          id: 'title',
          name: 'Title',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: 36,
            bold: true,
            color: '2E74B5'
          },
          paragraph: {
            spacing: {
              after: 200
            },
            alignment: AlignmentType.CENTER
          }
        },
        {
          id: 'subtitle',
          name: 'Subtitle',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: 24,
            color: '5B9BD5'
          },
          paragraph: {
            spacing: {
              after: 400
            },
            alignment: AlignmentType.CENTER
          }
        }
      ]
    }
  });

  // Get unique dates from events
  const getUniqueDates = (events) => {
    const dates = events.map(event => {
      const date = new Date(event.start);
      return date.toLocaleDateString();
    });
    return [...new Set(dates)].sort((a, b) => new Date(a) - new Date(b));
  };

  // Group events by date
  const groupedEvents = {};
  const uniqueDates = getUniqueDates(events);
  
  uniqueDates.forEach(date => {
    groupedEvents[date] = events.filter(event => 
      new Date(event.start).toLocaleDateString() === date
    ).sort((a, b) => new Date(a.start) - new Date(b.start));
  });

  // Document title and subtitle
  const title = new Paragraph({
    text: calendarName,
    style: 'title'
  });

  const subtitle = new Paragraph({
    text: `Calendar Schedule - Generated on ${new Date().toLocaleDateString()}`,
    style: 'subtitle'
  });

  const children = [title, subtitle];

  // Add events grouped by date
  uniqueDates.forEach(date => {
    // Add date header
    children.push(
      new Paragraph({
        text: new Date(date).toLocaleDateString([], { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        heading: HeadingLevel.HEADING_1,
        spacing: {
          before: 400,
          after: 200
        }
      })
    );

    // Create table for this date's events
    const tableRows = [];

    // Table header row
    tableRows.push(
      new TableRow({
        tableHeader: true,
        children: [
          new TableCell({
            width: {
              size: 30,
              type: WidthType.PERCENTAGE
            },
            shading: {
              fill: "F2F2F2"
            },
            children: [new Paragraph("Event")]
          }),
          new TableCell({
            width: {
              size: 20,
              type: WidthType.PERCENTAGE
            },
            shading: {
              fill: "F2F2F2"
            },
            children: [new Paragraph("Start Time")]
          }),
          new TableCell({
            width: {
              size: 20,
              type: WidthType.PERCENTAGE
            },
            shading: {
              fill: "F2F2F2"
            },
            children: [new Paragraph("End Time")]
          }),
          new TableCell({
            width: {
              size: 30,
              type: WidthType.PERCENTAGE
            },
            shading: {
              fill: "F2F2F2"
            },
            children: [new Paragraph("Calendar")]
          })
        ]
      })
    );

    // Add rows for each event on this date
    groupedEvents[date].forEach(event => {
      tableRows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph(event.title || 'Untitled Event')]
            }),
            new TableCell({
              children: [new Paragraph(
                new Date(event.start).toLocaleTimeString([], {
                  hour: '2-digit', 
                  minute: '2-digit'
                })
              )]
            }),
            new TableCell({
              children: [new Paragraph(
                new Date(event.end).toLocaleTimeString([], {
                  hour: '2-digit', 
                  minute: '2-digit'
                })
              )]
            }),
            new TableCell({
              children: [new Paragraph(event.provider || 'Unknown')]
            })
          ]
        })
      );
    });

    // Create and add the table to the document
    const table = new Table({
      rows: tableRows,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1 },
        bottom: { style: BorderStyle.SINGLE, size: 1 },
        left: { style: BorderStyle.SINGLE, size: 1 },
        right: { style: BorderStyle.SINGLE, size: 1 },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
        insideVertical: { style: BorderStyle.SINGLE, size: 1 }
      }
    });

    children.push(table);
  });

  // Add all content to the document
  doc.addSection({
    children: children
  });

  // Generate blob and save
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${calendarName.replace(/\s+/g, '-').toLowerCase()}.docx`);
};

/**
 * Export calendar view as an image
 */
const exportToImage = async () => {
  try {
    // First, we need to find the calendar element
    const calendarElement = document.querySelector('.fc');
    
    if (!calendarElement) {
      alert('Calendar view not found. Please make sure the calendar is visible.');
      return;
    }
    
    // Load html2canvas library dynamically if needed
    let html2canvas;
    if (!window.html2canvas) {
      const module = await import('html2canvas');
      html2canvas = module.default;
    } else {
      html2canvas = window.html2canvas;
    }
    
    // Configure the canvas options for better quality
    const canvas = await html2canvas(calendarElement, {
      scale: 2, // Higher scale for better quality
      useCORS: true, // Enable CORS for any images in the calendar
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true
    });
    
    // Convert canvas to blob
    canvas.toBlob(function(blob) {
      // Create URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = 'study-timetable.png';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png', 1.0);
  } catch (error) {
    console.error('Error exporting calendar as image:', error);
    alert('There was an error creating the image. Please try again.');
  }
};

/**
 * Triggers download of calendar data as a file
 * @param {string} icsData - Calendar data in iCalendar format
 * @param {string} filename - Name for the download file
 */
const downloadICS = (icsData, filename = 'study-schedule.ics') => {
  const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  // Create a link element and click it to trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Calendar Export component - provides UI for exporting calendar data
 */
const CalendarExport = ({ events = [], calendarName = "StudyAce Calendar" }) => {
  const [html2canvasLoaded, setHtml2canvasLoaded] = useState(false);

  // Load html2canvas library when component mounts
  useEffect(() => {
    const loadHtml2Canvas = async () => {
      try {
        if (!window.html2canvas) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          script.async = true;
          script.onload = () => setHtml2canvasLoaded(true);
          document.body.appendChild(script);
        } else {
          setHtml2canvasLoaded(true);
        }
      } catch (error) {
        console.error('Error loading html2canvas:', error);
      }
    };
    
    loadHtml2Canvas();
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleIcsExport = () => {
    if (events.length === 0) {
      alert('No events to export. Please add some events to your calendar first.');
      return;
    }
    
    // Convert events to iCalendar format
    const icsData = convertToICS(events, calendarName);
    
    // Download the file
    downloadICS(icsData, `${calendarName.replace(/\s+/g, '-').toLowerCase()}.ics`);
  };

  const handleWordExport = async () => {
    if (events.length === 0) {
      alert('No events to export. Please add some events to your calendar first.');
      return;
    }
    
    try {
      await exportToWord(events, calendarName);
    } catch (error) {
      console.error('Error exporting to Word:', error);
      alert('There was an error creating the Word document. Please try again.');
    }
  };

  const handleImageExport = async () => {
    if (!html2canvasLoaded) {
      alert('Image export is still loading. Please try again in a moment.');
      return;
    }
    
    await exportToImage();
  };

  // Calculate stats for display
  const totalEvents = events.length;
  const studyEvents = events.filter(e => e.provider === 'Study Schedule').length;
  const externalEvents = totalEvents - studyEvents;

  return (
    <div className="calendar-export p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 font-medium">Export Calendar</h5>
          <p className="text-sm text-gray-600 mb-0">
            Download your calendar with {totalEvents} events 
            ({studyEvents} study sessions, {externalEvents} imported events)
          </p>
        </div>
        <div>
          <ButtonGroup>
            <Button 
              variant="primary" 
              onClick={handleIcsExport}
              disabled={events.length === 0}
              className="d-flex align-items-center"
            >
              <Calendar size={16} className="me-2" />
              Calendar (.ics)
            </Button>
            <Button 
              variant="primary" 
              onClick={handleWordExport}
              disabled={events.length === 0}
              className="d-flex align-items-center"
            >
              <FileText size={16} className="me-2" />
              Word (.docx)
            </Button>
            <Button 
              variant="primary" 
              onClick={handleImageExport}
              disabled={events.length === 0 || !html2canvasLoaded}
              className="d-flex align-items-center"
            >
              <Image size={16} className="me-2" />
              Image (.png)
            </Button>
          </ButtonGroup>
        </div>
      </div>
      
      <div className="text-sm text-gray-600">
        <p className="mb-1">This will create a file that you can:</p>
        <ul className="ps-4">
          <li><strong>Calendar (.ics)</strong>: Import into Google Calendar, Outlook, Apple Calendar</li>
          <li><strong>Word (.docx)</strong>: Open with Word or Google Docs for printing</li>
          <li><strong>Image (.png)</strong>: Set as wallpaper, share on social media, or print</li>
        </ul>
      </div>
    </div>
  );
};

export default CalendarExport;